import { InfoOutlined, ManageAccounts, Person } from '@mui/icons-material';
import { Alert, AlertTitle, Box, Button, Grid, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef, GridSortModel } from '@mui/x-data-grid';
import { KivraLookupSearchFilter } from 'src/shared/models/kivra/kivraLookupSearchfilter';
import { Member } from 'src/modules/member/models/member';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { EmptyGridCell } from 'src/shared/components/atoms/emptyGridCell';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { getMembersWithKivraByInvoiceMissionId } from 'src/shared/services/memberService';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';

type InvoiceMissionKivraMembersProps = {
  invoiceMissionId: number;
  sendingtype: InvoiceSendingTypeIntegerEnum;
  kivraMembersCallback: (totalMembers: number) => void;
  searchFilter: KivraLookupSearchFilter;
  searchFilterCallback: (searchFilter: KivraLookupSearchFilter) => void;
  loadingCallback: (loading: boolean) => void;
};

export const InvoiceMissionKivraMembersDataGrid = (props: InvoiceMissionKivraMembersProps) => {
  const { invoiceMissionId, sendingtype, kivraMembersCallback, searchFilter, searchFilterCallback, loadingCallback } = props;
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<Member[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [displayDataGrid, setDisplayDataGrid] = useState<boolean>(false);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 25, 50, 100],
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });
  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    if (data.pageSize !== searchFilter.pageSize || data.page !== searchFilter.pageIndex) {
      setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
    }
  }, [data.page, data.pageSize, searchFilter.pageIndex, searchFilter.pageSize]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    loadingCallback(true);
    try {
      const res = await getMembersWithKivraByInvoiceMissionId(invoiceMissionId, sendingtype, searchFilter);
      const kivraResult = extendObjectWithId(res.data.results, 'member_id');
      if (isMounted()) {
        updateData('totalRows', kivraResult.length);
        setTimeout(() => {
          setRows(kivraResult);
        }, 100);
      }

      kivraMembersCallback(kivraResult.length);
      setDisplayDataGrid(true);
    } catch (e) {
      if (isMounted()) ConsoleHelper.log('erroer fetching kivra Members', e);
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
        setLoading(false);
      }
    }
  }, [invoiceMissionId, isMounted, kivraMembersCallback, loadingCallback, searchFilter, sendingtype]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const columns: GridColDef[] = [
    {
      field: 'memberNr',
      headerName: t('member:tableHeaders.memberNr'),
      flex: 0.7,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const { memberNr } = params.row;
        return <>{memberNr || <EmptyGridCell />}</>;
      },
    },
    {
      field: 'firstName',
      headerName: t('member:tableHeaders.firstName'),
      sortable: false,
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { isFamilyMember, isMainMember } = params.row;
        return (
          <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
            <span>{params.row.firstName || ''}</span>
            {isFamilyMember && !isMainMember && (
              <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                <Person fontSize='small' />
              </Tooltip>
            )}
            {isMainMember && (
              <Tooltip title={t('member:memberDetails.headMember')} arrow>
                <ManageAccounts fontSize='small' />
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'lastName',
      headerName: t('member:tableHeaders.lastName'),
      sortable: false,
      flex: 1.25,
      renderCell: (params: GridCellParams) => {
        const { lastName } = params.row;

        return <>{lastName || ''}</>;
      },
    },
    {
      field: 'email',
      headerName: t('member:tableHeaders.email'),
      sortable: false,
      align: 'left',
      headerAlign: 'left',
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        const { email } = params.row;

        return <>{email || ''}</>;
      },
    },
  ];

  return (
    <>
      {!displayDataGrid && (
        <>
          <Alert
            severity='warning'
            iconMapping={{
              warning: <InfoOutlined fontSize='inherit' />,
            }}
          >
            <AlertTitle>{t('invoice:invoiceMissionPage.kivra.searchKivraMembers')}</AlertTitle>

            <Grid container justifyContent='space-between'>
              <Grid item xs={12} md={7.5}>
                <Typography variant='body2'>{t('invoice:invoiceMissionPage.kivra.membersInfo')}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant='contained'
                  color='primary'
                  sx={{
                    mt: { xs: 2, md: 0 },
                    width: { xs: '100%', sm: 'auto' },
                  }}
                  onClick={fetchMembers}
                  disabled={loading}
                >
                  {t('invoice:invoiceMissionPage.kivra.searchKivraMembers')}
                </Button>
              </Grid>
            </Grid>
          </Alert>
          <Alert severity='info' sx={{ mt: 1 }}>
            <AlertTitle>{t('invoice:invoiceMissionPage.excludeSentOrPaid')}</AlertTitle>
          </Alert>
        </>
      )}
      {displayDataGrid && (
        <Box
          sx={{
            position: 'relative',
            '& .MuiPaper-root': {
              boxShadow: 'none',
            },
          }}
          ref={scrollRef}
        >
          <DataGrid<Member>
            autoHeight
            columns={columns}
            rows={rows}
            disablePointer
            disableSelectionOnClick
            dataModel={data}
            checkboxSelection={false}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            selectionModel={[]}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => {
              const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
              if (newSortModel.length) {
                setSortModel(newSortModel);
              } else {
                setSortModel([{ field: sortModel[0].field, sort }]);
              }
              searchFilterCallback({
                ...searchFilter,
                pageIndex: 0,
                sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
                sortDir: newSortModel.length ? newSortModel[0].sort : sort,
              });
              // fetchMembers(); - Aktiveras om möjlighet att sortera
            }}
            onSelectionModelChange={() => ConsoleHelper.log('')}
          />
        </Box>
      )}
    </>
  );
};
